import React, { ReactNode, forwardRef } from 'react';

import { Progress as RadixProgress } from '@radix-ui/themes';
import type { ComponentPropsWithoutRef } from 'react';
import { useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';

import { HelpText } from './HelpText';

type RadixProgressProps = ComponentPropsWithoutRef<typeof RadixProgress>;

const sizeMapping = {
  xsmall: '1',
  small: '1',
  medium: '2',
  large: '3',
} as const;

function resolveColor(
  color: string,
  accentColor?: string
): RadixProgressProps['color'] {
  const allowedColors = [
    'gray',
    'blue',
    'red',
    'green',
    'teal',
    'ruby',
    'amber',
    'orange',
    'crimson',
    'jade',
    'pink',
    'plum',
    'purple',
    'violet',
    'indigo',
  ];
  switch (color) {
    case 'primary':
      return allowedColors.includes(accentColor || '')
        ? (accentColor as RadixProgressProps['color'])
        : 'indigo';
    case 'error':
      return 'crimson';
    case 'success':
      return 'jade';
    case 'warning':
      return 'amber';
    case 'info':
      return 'blue';
    case 'default':
    default:
      return 'gray';
  }
}

const variantMapping = {
  surface: 'surface',
  classic: 'classic',
  soft: 'soft',
  ghost: 'ghost',
} as const;

export interface ProgressProps
  extends Omit<RadixProgressProps, 'size' | 'color' | 'variant'> {
  value: number;
  max?: number;
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'success'
    | 'warning'
    | 'info';
  variant?: 'surface' | 'classic' | 'soft' | 'ghost';
  label?: ReactNode;
  helpText?: string;
  error?: boolean;
  className?: string;
  highContrast?: boolean;
}

/**
 * Progress component is a design system adapter for Radix Progress.
 * It supports label, help text, error, size, color, and variant props.
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      value,
      max = 100,
      size = 'medium',
      color = 'default',
      variant = 'surface',
      label,
      helpText,
      error = false,
      className,
      highContrast = true,
      ...props
    },
    ref
  ) => {
    const mappedSize = sizeMapping[size] || sizeMapping.medium;
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const mappedColor = error
      ? 'crimson'
      : (resolveColor(
          String(color),
          accentColor
        ) as RadixProgressProps['color']);
    const mappedVariant = variantMapping[variant] || variantMapping.surface;

    return (
      <div className={clsx('progress-wrapper', className)}>
        {label && <div className="progress-label">{label}</div>}
        <RadixProgress
          value={value}
          max={max}
          highContrast={highContrast}
          size={mappedSize as RadixProgressProps['size']}
          color={mappedColor as RadixProgressProps['color']}
          variant={mappedVariant as RadixProgressProps['variant']}
          ref={ref}
          className={clsx(
            `progress-size-${size}`,
            `progress-color-${color}`,
            `progress-variant-${variant}`,
            { error },
            'progress-root'
          )}
          data-error={error}
          {...props}
        />
        {helpText && <HelpText error={error}>{helpText}</HelpText>}
      </div>
    );
  }
);

Progress.displayName = 'Progress';
